import React from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Chip, Grid, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';

import { getDemsConnectors } from 'app/main/configs/DefaultModifiedModelAdd';
import { Icon, useSettings, useUserData, Utils } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';
import {
	cancelSend,
	checkSendStatus,
	deleteMailchimpList,
	loadDefaultRecipients,
	retrieveStats,
	sendDem,
	setConnector,
	unschedule,
	type DemsManagerActionParams
} from '../../components/DemsManagerActions';
import { showDemsSettingsDialog } from '../../components/DemsSettingsDialog';

interface DemsDetailsSectionManagerProps {
	model: any;
	updateRefresh: VoidFunction;
}

export default function DemsDetailsSectionManager({ model, updateRefresh }: DemsDetailsSectionManagerProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const { user_uid: userUid } = useUserData();
	const intl = useIntl();

	const connectors = getDemsConnectors(sysSettings);
	const activeConnectors = connectors.filter(connector => connector.isActive);

	const hasAccessToken = sysSettings[`mailing_${model.dems_connector}_access_token`];

	const [activeStep, setActiveStep] = React.useState(model.dems_connector ? model.dems_dmst_id || 0 : 0);

	const handlePrevious = () => {
		setActiveStep(prevActiveStep => prevActiveStep - 1);
		updateRefresh();
	};

	const handleNext = () => {
		setActiveStep(prevActiveStep => prevActiveStep + 1);
		updateRefresh();
	};

	const actionParams: DemsManagerActionParams = {
		dispatch,
		t,
		demsConnector: model.dems_connector,
		demsUid: model.dems_uid,
		userUid
	};

	const changeConnector = (demsConnector, onSuccess) => setConnector({ ...actionParams, demsConnector }, onSuccess);

	const steps = [
		{
			title: t('DEMS_SELECT_CONNECTOR'),
			description: t('DEMS_SELECT_CONNECTOR_DESCRIPTION'),
			content: connectors.map(connector => (
				<Grid key={connector.name}>
					<Button
						color="primary"
						startIcon={<Icon pack={connector.iconPack} icon={connector.icon} />}
						onClick={() => changeConnector(connector.name, handleNext)}
						disabled={!connector.isActive}
					>
						{t(`DEMS_CONNECTOR_${connector.name}`)}
					</Button>
				</Grid>
			))
		},
		{
			title: t('DEMS_ADD_RECIPIENTS'),
			description: !hasAccessToken ? (
				<Chip
					label={t('DEMS_CONNECTOR_ERROR_NOT_LOGGED')}
					icon={<Icon icon="warning-outline" />}
					color="error"
				/>
			) : model.dems_connector ? (
				t(`DEMS_${model.dems_connector}_ADD_RECIPIENTS_DESCRIPTION`)
			) : undefined,
			content: hasAccessToken && (
				<>
					<Grid>
						<Button
							variant="text"
							color="primary"
							startIcon={<Icon icon="email-edit-outline" />}
							onClick={() => loadDefaultRecipients(actionParams, updateRefresh)}
						>
							{t('DEMS_UPLOAD_DEFAULT_RECIPIENTS')}
						</Button>
					</Grid>
					{activeConnectors.length > 1 && (
						<Grid>
							<Button
								variant="text"
								color="primary"
								onClick={() => changeConnector(null, handlePrevious)}
							>
								{t('DEMS_ACTION_BACK')}
							</Button>
						</Grid>
					)}
					<Grid>
						<Button
							variant="contained"
							color="primary"
							startIcon={<Icon icon="publish" />}
							disabled={!hasAccessToken}
							onClick={() => dispatch(showDemsSettingsDialog({ model, onSuccess: handleNext }))}
						>
							{t('DEMS_UPLOAD_RECIPIENTS')}
						</Button>
					</Grid>
				</>
			)
		},
		{
			title: t('DEMS_SEND'),
			description: model.dems_connector ? t(`DEMS_${model.dems_connector}_SEND_DESCRIPTION`) : undefined,
			content: (
				<>
					<Grid>
						<Button color="primary" onClick={() => cancelSend(actionParams, handlePrevious)}>
							{t('DEMS_ACTION_CANCELSEND')}
						</Button>
					</Grid>
					<Grid>
						<Button variant="contained" color="primary" onClick={() => sendDem(actionParams, handleNext)}>
							{t('DEMS_ACTION_SEND')}
						</Button>
					</Grid>
				</>
			)
		},
		{
			title: t('DEMS_SENDING'),
			description: intl.formatMessage(
				{
					id: 'demsManager.sendingDescription',
					defaultMessage: t('DEMS_SENDING_DESCRIPTION')
				},
				{
					date: intl.formatDate(Utils.parseDate(model.dems_date)?.toDate(), {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit'
					})
				}
			),
			content: (
				<>
					<Grid>
						<Button
							color="primary"
							onClick={() => checkSendStatus(actionParams, handleNext)}
							disabled={moment().diff(Utils.parseDate(model.dems_date), 'm') <= 0}
						>
							{t('DEMS_ACTION_CHECK_SEND_STATUS')}
						</Button>
					</Grid>
					<Grid>
						<Button color="primary" onClick={() => unschedule(actionParams, handlePrevious)}>
							{t('DEMS_ACTION_UNSCHEDULE')}
						</Button>
					</Grid>
				</>
			)
		},
		{
			title: t('DEMS_DOWNLOAD_STATS'),
			description: model.dems_connector
				? t(`DEMS_${model.dems_connector}_DOWNLOAD_STATS_DESCRIPTION`)
				: undefined,
			content: (
				<Grid>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Icon icon="download" />}
						onClick={() => retrieveStats(actionParams, updateRefresh)}
					>
						{t('DEMS_DOWNLOAD')}
					</Button>
				</Grid>
			)
		}
	];

	return (
		<DetailsSectionWrapper
			title={t('DEMS_MANAGER_SECTION')}
			subheader={model.dems_connector ? t(`DEMS_CONNECTOR_${model.dems_connector}`) : undefined}
		>
			<Stepper activeStep={activeStep} orientation="vertical" className="bg-transparent p-0">
				{steps.map((step, i) => (
					<Step key={i}>
						<StepLabel>{step.title}</StepLabel>
						<StepContent>
							<Grid container justifyContent="flex-end" spacing={1}>
								<Grid size={12}>
									<Typography>{step.description}</Typography>
								</Grid>
								{step.content}
							</Grid>
						</StepContent>
					</Step>
				))}
			</Stepper>
			{[5, 6].includes(model.dems_dmst_id) && (
				<Grid container spacing={1} className="pt-8">
					<Grid>
						<Button
							variant="contained"
							startIcon={<Icon icon="download" />}
							onClick={() => retrieveStats(actionParams, updateRefresh)}
						>
							{t('DEMS_UPDATE_STATS')}
						</Button>
					</Grid>
					{model.dems_dmst_id === 5 && model.dems_connector === 'mailchimp' && (
						<Grid>
							<Button
								variant="contained"
								startIcon={<Icon icon="delete" />}
								onClick={() => deleteMailchimpList(actionParams, handleNext)}
							>
								{t('DEMS_MAILCHIMP_DELETE_LIST')}
							</Button>
						</Grid>
					)}
				</Grid>
			)}
		</DetailsSectionWrapper>
	);
}
